'use client';

import * as React from 'react';

import { useIsMobile } from '@heidi/utils';
import { usePinnedState } from '../../hooks/use-page-shell-state';

import {
  type PanelContextValue,
  type PanelMobileView,
  type PanelProviderProps,
  type PanelRegistration,
} from './panel.types';

// ============================================================================
// Context
// ============================================================================

const PanelContext = React.createContext<PanelContextValue | null>(null);

/**
 * Hook to access the panel context.
 * Must be used within a PanelProvider.
 *
 * @throws Error if used outside of PanelProvider
 *
 * @example
 * ```tsx
 * const { isPinned, setIsPinned, floatingPanelId } = usePanelContext();
 * ```
 */
function usePanelContext(): PanelContextValue {
  const context = React.useContext(PanelContext);
  if (!context) {
    throw new Error('usePanelContext must be used within a PanelProvider.');
  }
  return context;
}

// ============================================================================
// Provider
// ============================================================================

/**
 * Provider component for panel state management.
 * Wraps PageShell children to provide panel context.
 *
 * @example
 * ```tsx
 * <PanelProvider pageId={router.pathname}>
 *   <PageShell.Sidebar>...</PageShell.Sidebar>
 *   <PageShell.Main>...</PageShell.Main>
 * </PanelProvider>
 * ```
 */
const PanelProvider = ({ pageId, children }: PanelProviderProps) => {
  // Mobile detection using existing hook
  const isMobile = useIsMobile();

  // Global pinned state from Jotai atom (session-persistent)
  const { isPinned, setIsPinned } = usePinnedState();

  // Floating panel state (ephemeral)
  const [floatingPanelId, setFloatingPanelId] = React.useState<string | null>(null);

  // Active panel content and action (for mobile)
  const [activePanelContent, setActivePanelContent] =
    React.useState<React.ReactNode | null>(null);
  const [activePanelAction, setActivePanelAction] =
    React.useState<React.ReactNode | null>(null);

  // Registered panels map
  const [registeredPanels, setRegisteredPanels] = React.useState<
    Map<string, PanelRegistration>
  >(() => new Map());

  // Mobile view state
  const [mobileView, setMobileView] = React.useState<PanelMobileView>('sidebar');

  // Panel ref for focus management
  const panelElementRef = React.useRef<HTMLElement | null>(null);

  // Register panel element for focus management
  const setPanelRef = React.useCallback((element: HTMLElement | null) => {
    panelElementRef.current = element;
  }, []);

  // Focus the panel (for accessibility after opening)
  const focusPanel = React.useCallback(() => {
    // Use requestAnimationFrame to ensure the panel has rendered
    requestAnimationFrame(() => {
      panelElementRef.current?.focus();
    });
  }, []);

  // Register a panel
  const registerPanel = React.useCallback(
    (id: string, content: React.ReactNode, action?: React.ReactNode) => {
      setRegisteredPanels((prev) => {
        const registration: PanelRegistration = { content, action };
        // Check if already registered with same content and action references
        const existing = prev.get(id);
        if (existing && existing.content === content && existing.action === action) {
          return prev;
        }
        const next = new Map(prev);
        next.set(id, registration);
        return next;
      });
    },
    []
  );

  // Unregister a panel
  const unregisterPanel = React.useCallback((id: string) => {
    setRegisteredPanels((prev) => {
      if (!prev.has(id)) {
        return prev;
      }
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  // Check if panel content is available for the CURRENT page
  // This ensures the panel trigger only shows when there's content to pin
  const hasPanelContent = registeredPanels.has(pageId);

  // Reset mobile view when navigating to a new page
  React.useEffect(() => {
    setMobileView('sidebar');
  }, [pageId]);

  const contextValue = React.useMemo<PanelContextValue>(
    () => ({
      isPinned,
      setIsPinned,
      focusPanel,
      setPanelRef,
      floatingPanelId,
      setFloatingPanelId,
      activePanelContent,
      setActivePanelContent,
      activePanelAction,
      setActivePanelAction,
      registeredPanels,
      registerPanel,
      unregisterPanel,
      isMobile,
      mobileView,
      setMobileView,
      pageId,
      hasPanelContent,
    }),
    [
      isPinned,
      setIsPinned,
      focusPanel,
      setPanelRef,
      floatingPanelId,
      activePanelContent,
      activePanelAction,
      registeredPanels,
      registerPanel,
      unregisterPanel,
      isMobile,
      mobileView,
      pageId,
      hasPanelContent,
    ]
  );

  return <PanelContext.Provider value={contextValue}>{children}</PanelContext.Provider>;
};
PanelProvider.displayName = 'PanelProvider';

// ============================================================================
// Exports
// ============================================================================

export { PanelContext, PanelProvider, usePanelContext };
